import useAnimatedPress from '@ui/animated/useAnimatedPress'
import { colors } from '@ui/colors'
import Icon, { IconName } from '@ui/elements/Icon'
import Typography from '@ui/typography'
import FormattedText from '@ui/typography/FormattedText'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Control, FieldValues, Path, useController } from 'react-hook-form'
import { Pressable, View } from 'react-native'
import Animated, {
  interpolate,
  interpolateColor,
  SharedValue,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated'

export type CheckboxProps = {
  icon?: IconName
  label?: string
  value?: boolean
  onChange?: () => void
  disabled?: boolean
  noninteractive?: boolean
  colorActive?: string
  colorInactive?: string
  desc?: string
  error?: boolean
  required?: boolean
}

export const CheckboxIcon = ({
  checked,
  error,
  colorActive = colors.text,
  colorInactive = colors.text,
  icon = 'check',
}: {
  icon?: IconName
  checked: SharedValue<number>
  error: SharedValue<number>
  colorActive?: string
  colorInactive?: string
}) => {
  const borderStyle = useAnimatedStyle(() => {
    const errorInactive = interpolateColor(
      error?.value || 0,
      [0, 1],
      [colorInactive, colors.red[600]]
    )

    const errorActive = interpolateColor(
      error?.value || 0,
      [0, 1],
      [colorActive, colors.red[600]]
    )

    const color = interpolateColor(
      checked.value,
      [0, 1],
      [errorInactive, errorActive]
    )

    return {
      borderWidth: 3,
      borderColor: color,
    }
  })

  const iconContainerStyle = useAnimatedStyle(() => {
    return {
      backgroundColor: interpolateColor(
        checked.value,
        [0, 1],
        [colors.white0, colorActive]
      ),
    }
  })

  const iconStyle = useAnimatedStyle(() => {
    return {
      opacity: checked.value,
      transform: [
        {
          scale: interpolate(checked.value, [0, 0.5, 1], [0, 0, 1]),
        },
      ],
    }
  })

  return (
    <View className="flex h-8 w-8 flex-row items-center justify-center">
      <Animated.View
        style={[iconContainerStyle, borderStyle]}
        className="flex-0 h-7 w-7 items-center justify-center rounded-sm"
      >
        <Animated.View style={iconStyle}>
          <Icon size={20} strokeWidth={3} name={icon} color={colors.white} />
        </Animated.View>
      </Animated.View>
    </View>
  )
}

const Checkbox = ({
  icon = 'check',
  label,
  value,
  onChange,
  disabled,
  noninteractive,
  colorActive = colors.text,
  colorInactive,
  desc,
  error: errorProp,
  required,
}: CheckboxProps) => {
  const checked = useSharedValue(value ? 1 : 0)
  const error = useSharedValue(errorProp ? 1 : 0)

  const { animation, onPressIn, onPressOut } = useAnimatedPress()

  const outerStyle = useAnimatedStyle(() => ({
    opacity: disabled ? 0.25 : interpolate(animation.value, [0, 1], [1, 1]),
  }))

  const pressableStyle = useAnimatedStyle(() => ({
    transform: [
      {
        scale: interpolate(animation.value, [0, 1], [1, 0.75]),
      },
    ],
  }))

  const styleLabel = useAnimatedStyle(() => ({
    color: interpolateColor(
      error.value,
      [0, 1],
      [colorActive, colors.red[600]]
    ),
  }))

  const styleDesc = useAnimatedStyle(() => ({
    color: interpolateColor(error.value, [0, 1], [colors.text50, colors.red50]),
  }))

  useEffect(() => {
    checked.set(withSpring(value ? 1 : 0))
  }, [checked, value])

  useEffect(() => {
    error.set(withSpring(errorProp ? 1 : 0))
  }, [error, errorProp])

  return (
    <View className="flex flex-col gap-2">
      <View className="flex flex-row items-center">
        <Pressable
          onPressIn={onPressIn}
          onPressOut={onPressOut}
          onPress={onChange}
          disabled={disabled || noninteractive}
          hitSlop={4}
        >
          <Animated.View
            style={outerStyle}
            className="flex-0 flex-row items-center gap-4"
          >
            <Animated.View style={pressableStyle}>
              <CheckboxIcon
                checked={checked}
                error={error}
                colorActive={colorActive}
                icon={icon}
                colorInactive={colorInactive}
              />
            </Animated.View>
            {label && (
              <View className="flex flex-row items-center gap-1">
                <Typography variant="SMB" style={styleLabel}>
                  {label}
                </Typography>
                {required && (
                  <Typography
                    variant="SMB"
                    className="h-1 w-1 rounded-full text-orange-600"
                    style={[{ top: -10 }]}
                  >
                    *
                  </Typography>
                )}
              </View>
            )}
          </Animated.View>
        </Pressable>
      </View>
      {desc && (
        <View className="flex flex-1" style={{ paddingLeft: 48 }}>
          <FormattedText
            variant="SM"
            className="text-left color-desc"
            classNameBold="color-text"
            text={desc}
            style={styleDesc}
          />
        </View>
      )}
    </View>
  )
}

export const ControlledCheckbox = ({
  defaultValue = false,
  onChange,
  ...rest
}: Omit<CheckboxProps, 'value'> & {
  defaultValue?: boolean
}) => {
  const [value, setValue] = useState(defaultValue)
  const handlePress = useCallback(() => {
    onChange?.()
    setValue((v) => !v)
  }, [onChange])

  return <Checkbox {...rest} value={value} onChange={handlePress} />
}

export const FormCheckbox = <T extends FieldValues>({
  control,
  name,
  ...rest
}: {
  control: Control<T>
  name: Path<T>
} & Omit<CheckboxProps, 'value' | 'onChange'>) => {
  const { field, fieldState } = useController({
    name,
    control,
  })

  return (
    <Checkbox
      {...rest}
      error={!!fieldState.error}
      value={field.value}
      onChange={() => field.onChange(!field.value)}
    />
  )
}
export default Checkbox
